import { useCallback, useEffect, useRef, useState } from "react";
import { refreshManufacturingPricingLayersMaterials } from "@/lib/manufacturingClient";
import type { CogsMaterialsPatch } from "@/lib/manufacturing/cogsCatalogView";
import { COGS_CALC_BATCH } from "@/lib/manufacturing/cogsPaging";
import type { MfgPricingLayerGridRow } from "@/lib/manufacturing/types";

export type CogsRefreshScope = "page" | "later" | "all";

export type CogsPageCalcState = {
  done: number;
  total: number;
  phase: "calculating" | "saving";
  /** Later pages are being saved while the open page stays usable. */
  background: boolean;
};

type Scope = {
  plantId: string;
  series: string[];
  roofStyle: string[];
  sizeBand: string[];
  pageSize: number;
  pageOffset: number;
  totalCount: number;
};

function recipeIds(rows: MfgPricingLayerGridRow[], onlyUnsaved: boolean): string[] {
  return rows
    .filter((row) => row.subject_kind === "recipe" && (!onlyUnsaved || !row.materials_computed_at))
    .map((row) => row.subject_id);
}

function scopeKey(scope: Scope): string {
  return [
    scope.plantId,
    scope.pageSize,
    scope.series.join(","),
    scope.roofStyle.join(","),
    scope.sizeBand.join(","),
  ].join("|");
}

/**
 * Calculates material cost for the visible page and saves it.
 * A background pass can keep saving later pages without covering the screen.
 */
export function useCogsPageMaterialsCalc(args: {
  enabled: boolean;
  listBusy: boolean;
  rows: MfgPricingLayerGridRow[];
  /** Filtered catalog, including rows that are not on the open page. */
  allRows: MfgPricingLayerGridRow[];
  plantId: string;
  series: string[];
  roofStyle: string[];
  sizeBand: string[];
  pageSize: number;
  pageOffset: number;
  totalCount: number;
  applySummaries: (summaries: CogsMaterialsPatch[]) => void;
  onError: (message: string) => void;
}) {
  const [pageCalc, setPageCalc] = useState<CogsPageCalcState | null>(null);
  const [backgroundFill, setBackgroundFill] = useState(false);
  const rowsRef = useRef(args.rows);
  rowsRef.current = args.rows;
  const allRowsRef = useRef(args.allRows);
  allRowsRef.current = args.allRows;
  const applyRef = useRef(args.applySummaries);
  applyRef.current = args.applySummaries;
  const onErrorRef = useRef(args.onError);
  onErrorRef.current = args.onError;
  const scopeRef = useRef<Scope>({
    plantId: args.plantId,
    series: args.series,
    roofStyle: args.roofStyle,
    sizeBand: args.sizeBand,
    pageSize: args.pageSize,
    pageOffset: args.pageOffset,
    totalCount: args.totalCount,
  });
  scopeRef.current = {
    plantId: args.plantId,
    series: args.series,
    roofStyle: args.roofStyle,
    sizeBand: args.sizeBand,
    pageSize: args.pageSize,
    pageOffset: args.pageOffset,
    totalCount: args.totalCount,
  };
  const runId = useRef(0);
  const lastAutoKey = useRef("");
  const backgroundFillRef = useRef(false);

  const saveChunk = useCallback(async (ids: string[], my: number, force: boolean) => {
    for (let i = 0; i < ids.length; i += COGS_CALC_BATCH) {
      if (runId.current !== my) return false;
      const chunk = ids.slice(i, i + COGS_CALC_BATCH);
      const result = await refreshManufacturingPricingLayersMaterials({
        recipe_ids: chunk,
        plant_id: scopeRef.current.plantId || undefined,
        limit: chunk.length,
        force,
      });
      if (runId.current !== my) return false;
      if (!result.ok) {
        onErrorRef.current(result.error);
        return false;
      }
      applyRef.current(result.data.summaries);
    }
    return true;
  }, []);

  const fillLaterPages = useCallback(
    async (my: number) => {
      const origin = scopeRef.current;
      const started = scopeKey(origin);
      const pageIds = new Set(recipeIds(rowsRef.current, false));
      const ids = recipeIds(allRowsRef.current, true).filter((id) => !pageIds.has(id));
      if (ids.length === 0) return;
      let saved = 0;
      setPageCalc({ done: 0, total: ids.length, phase: "calculating", background: true });
      for (let i = 0; i < ids.length; i += COGS_CALC_BATCH) {
        if (runId.current !== my || !backgroundFillRef.current) return;
        if (scopeKey(scopeRef.current) !== started) return;
        const chunk = ids.slice(i, i + COGS_CALC_BATCH);
        const result = await refreshManufacturingPricingLayersMaterials({
          recipe_ids: chunk,
          plant_id: origin.plantId || undefined,
          limit: chunk.length,
          force: false,
        });
        if (runId.current !== my) return;
        if (!result.ok) {
          onErrorRef.current(result.error);
          return;
        }
        applyRef.current(result.data.summaries);
        saved += chunk.length;
        setPageCalc({
          done: saved,
          total: ids.length,
          phase: "calculating",
          background: true,
        });
      }
    },
    [],
  );

  const calculateIds = useCallback(
    async (ids: string[], force: boolean) => {
      if (ids.length === 0) return;
      const my = ++runId.current;
      setPageCalc({ done: 0, total: ids.length, phase: "calculating", background: false });
      let savedAny = false;
      try {
        for (let i = 0; i < ids.length; i += COGS_CALC_BATCH) {
          if (runId.current !== my) return;
          const chunk = ids.slice(i, i + COGS_CALC_BATCH);
          const ok = await saveChunk(chunk, my, force);
          if (runId.current !== my) return;
          if (!ok) return;
          savedAny = true;
          setPageCalc({
            done: Math.min(i + chunk.length, ids.length),
            total: ids.length,
            phase: "calculating",
            background: false,
          });
        }
        if (runId.current !== my || !savedAny) return;
        setPageCalc({ done: ids.length, total: ids.length, phase: "saving", background: false });
        if (runId.current === my && backgroundFillRef.current) {
          await fillLaterPages(my);
        }
      } catch (err) {
        if (runId.current === my) {
          onErrorRef.current(err instanceof Error ? err.message : "Could not calculate material costs.");
        }
      } finally {
        if (runId.current === my) {
          backgroundFillRef.current = false;
          setBackgroundFill(false);
          setPageCalc(null);
        }
      }
    },
    [fillLaterPages, saveChunk],
  );

  useEffect(() => {
    if (!args.enabled || args.listBusy) return;
    const ids = recipeIds(args.rows, true);
    if (ids.length === 0) return;
    const key = `${args.plantId}|${args.pageOffset}|${ids.join(",")}`;
    if (lastAutoKey.current === key) return;
    lastAutoKey.current = key;
    void calculateIds(ids, false);
  }, [args.enabled, args.listBusy, args.plantId, args.pageOffset, args.rows, calculateIds]);

  const calculateVisiblePage = useCallback((scope: CogsRefreshScope = "page") => {
    const page = recipeIds(rowsRef.current, false);
    if (scope === "page") return calculateIds(page, true);
    if (scope === "all") return calculateIds(recipeIds(allRowsRef.current, false), true);
    const later = recipeIds(allRowsRef.current.slice(scopeRef.current.pageOffset), false);
    return calculateIds(later.length > 0 ? later : page, true);
  }, [calculateIds]);

  const continueBackground = useCallback(() => {
    backgroundFillRef.current = true;
    setBackgroundFill(true);
  }, []);

  return { pageCalc, backgroundFill, calculateVisiblePage, continueBackground };
}
